import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Main from 'layouts/Main';
import Container from 'components/Container';
import SEO from 'components/SEO';
import { SearchResults as SharedSearchResults } from '@akyga/shared-ui/SearchResults';
import { useTranslation } from 'react-i18next';
import useProductData from 'Hooks/useProductData';
import useCategories from 'Hooks/useCategories';
import useQuery from 'Hooks/useQueryParam';
import { generateProductCategoryUrl } from 'Utilities/urlHelpers';
import { SearchFilter } from 'types/item';

const EMPTY_FILTERS: SearchFilter[] = [];

// akygabattery's own root categories - keeps search results scoped to
// battery-relevant products rather than the full shared catalog.
const BATTERY_PARENT_IDS = [50, 51];

const SearchResults = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQuery();
  const q = queryParams.get('q') ?? '';
  const sortKey = queryParams.get('sort') ?? 'relevance';

  // akygabattery is a dedicated battery-only site, so its own search
  // results always restrict to its root categories - unconditionally, not
  // just when the URL happens to carry a parentIds param (a user landing
  // on /search?q=X via a plain link should still never see non-battery
  // products here).
  const {
    data,
    total,
    categoryFacets,
    loading,
    error,
  } = useProductData(null, null, EMPTY_FILTERS, q, null, BATTERY_PARENT_IDS);
  const { data: categories = [] } = useCategories();

  const handleSortChange = (nextSortKey: string) => {
    const params = new URLSearchParams(location.search);
    if (nextSortKey === 'relevance') {
      params.delete('sort');
    } else {
      params.set('sort', nextSortKey);
    }
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
  };

  const handleSelectLeafCategory = (categoryName: string) => {
    navigate(
      `${generateProductCategoryUrl(categoryName)}?q=${encodeURIComponent(q)}`,
    );
  };

  return (
    <Main>
      <SEO title={t('searchResults.title')} url="/search" />
      <Container>
        <SharedSearchResults
          q={q}
          sortKey={sortKey}
          onSortChange={handleSortChange}
          onSelectLeafCategory={handleSelectLeafCategory}
          data={data}
          total={total}
          categoryFacets={categoryFacets}
          loading={loading}
          error={error}
          categories={categories}
        />
      </Container>
    </Main>
  );
};

export default SearchResults;
